import { useState, type ReactNode } from "react";
import { Maximize2, RectangleHorizontal, Square } from "lucide-react";

export type ModalSize = "md" | "lg" | "full";

const SIZES: Record<ModalSize, { w: string; h: string; label: string; icon: ReactNode }> = {
  md: { w: "max-w-2xl", h: "max-h-[88vh]", label: "中", icon: <Square className="h-3.5 w-3.5" /> },
  lg: { w: "max-w-4xl", h: "max-h-[88vh]", label: "大", icon: <RectangleHorizontal className="h-3.5 w-3.5" /> },
  full: { w: "max-w-[96vw]", h: "max-h-[92vh]", label: "全屏", icon: <Maximize2 className="h-3.5 w-3.5" /> },
};

const SIZE_ORDER: ModalSize[] = ["md", "lg", "full"];

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  defaultSize = "md",
  sizeControls = false,
}: {
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  defaultSize?: ModalSize;
  sizeControls?: boolean;
}) {
  const [size, setSize] = useState<ModalSize>(defaultSize);
  const s = SIZES[size];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`flex ${s.h} w-full ${s.w} flex-col rounded-xl border bg-card p-5 shadow-lg`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">{title}</h2>
            {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          {sizeControls && (
            <div className="flex shrink-0 items-center gap-0.5 rounded-md border bg-muted/40 p-0.5">
              {SIZE_ORDER.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSize(k)}
                  aria-pressed={size === k}
                  aria-label={SIZES[k].label}
                  title={SIZES[k].label}
                  className={`rounded p-1 ${
                    size === k ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {SIZES[k].icon}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-3 flex-1 overflow-y-auto pr-1">{children}</div>
      </div>
    </div>
  );
}
